
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const animalGroups = [
  { value: "01", label: "Grupo 01", animal: "Avestruz" },
  { value: "02", label: "Grupo 02", animal: "Águia" },
  { value: "03", label: "Grupo 03", animal: "Burro" },
  { value: "04", label: "Grupo 04", animal: "Borboleta" },
  { value: "05", label: "Grupo 05", animal: "Cachorro" },
  { value: "06", label: "Grupo 06", animal: "Cabra" },
  { value: "07", label: "Grupo 07", animal: "Carneiro" },
  { value: "08", label: "Grupo 08", animal: "Camelo" },
  { value: "09", label: "Grupo 09", animal: "Cobra" },
  { value: "10", label: "Grupo 10", animal: "Coelho" },
  { value: "11", label: "Grupo 11", animal: "Cavalo" },
  { value: "12", label: "Grupo 12", animal: "Elefante" },
  { value: "13", label: "Grupo 13", animal: "Galo" },
  { value: "14", label: "Grupo 14", animal: "Gato" },
  { value: "15", label: "Grupo 15", animal: "Jacaré" },
  { value: "16", label: "Grupo 16", animal: "Leão" },
  { value: "17", label: "Grupo 17", animal: "Macaco" },
  { value: "18", label: "Grupo 18", animal: "Porco" },
  { value: "19", label: "Grupo 19", animal: "Pavão" },
  { value: "20", label: "Grupo 20", animal: "Peru" },
  { value: "21", label: "Grupo 21", animal: "Touro" },
  { value: "22", label: "Grupo 22", animal: "Tigre" },
  { value: "23", label: "Grupo 23", animal: "Urso" },
  { value: "24", label: "Grupo 24", animal: "Veado" },
  { value: "25", label: "Grupo 25", animal: "Vaca" }
];

interface BicharGroupSelectorProps {
  selectedGroups: string[];
  onGroupsChange: (groups: string[]) => void;
  disabled?: boolean;
}

const BicharGroupSelector: React.FC<BicharGroupSelectorProps> = ({ 
  selectedGroups, 
  onGroupsChange, 
  disabled = false 
}) => {
  const handleGroupSelect = (groupValue: string) => {
    if (groupValue === "all") {
      onGroupsChange([]);
      return;
    }
    
    if (selectedGroups.includes(groupValue)) {
      // Remove se já estiver selecionado
      onGroupsChange(selectedGroups.filter(g => g !== groupValue));
    } else {
      // Adiciona se não estiver selecionado
      onGroupsChange([...selectedGroups, groupValue]);
    }
  };

  const removeGroup = (groupValue: string) => {
    onGroupsChange(selectedGroups.filter(g => g !== groupValue));
  };

  const getDisplayText = () => {
    if (selectedGroups.length === 0) {
      return "Todos os grupos";
    }
    if (selectedGroups.length === 1) {
      const group = animalGroups.find(g => g.value === selectedGroups[0]);
      return group ? `${group.animal}` : "Selecionado";
    }
    return `${selectedGroups.length} bichos selecionados`;
  };

  return (
    <Card className={disabled ? "opacity-50" : ""}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-4 h-4" />
          Grupos do Bicho
        </CardTitle>
        <CardDescription>
          Selecione um ou vários bichos para gerar palpites combinados
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Select 
          onValueChange={handleGroupSelect}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder={getDisplayText()} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              Todos os grupos
            </SelectItem>
            {animalGroups.map((group) => (
              <SelectItem key={group.value} value={group.value}>
                {group.label} - {group.animal}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Badges dos grupos selecionados */}
        {selectedGroups.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedGroups.map((groupValue) => {
              const group = animalGroups.find(g => g.value === groupValue);
              return group ? (
                <Badge key={groupValue} variant="secondary" className="flex items-center gap-1">
                  {group.animal}
                  <X 
                    className="w-3 h-3 cursor-pointer hover:text-red-500" 
                    onClick={() => removeGroup(groupValue)}
                  />
                </Badge>
              ) : null;
            })}
          </div>
        )}

        {selectedGroups.length > 1 && (
          <p className="text-sm text-muted-foreground">
            Os palpites serão gerados combinando dezenas dos {selectedGroups.length} bichos selecionados
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default BicharGroupSelector;
